import {
  type UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useEffect } from "react"

import {
  type Body_login_login_access_token as AccessToken,
  AuthenticationService,
  type UserPublic,
} from "@/client"
import useAuthStore, { type AuthUser } from "@/stores/authStore"
import useCustomToast from "./useCustomToast"

// Cookie name (must match backend) — used by isLoggedIn() for non-httponly fallback
const ACCESS_TOKEN_COOKIE_NAME = "access_token"

const isLoggedIn = () => {
  // Check for access_token cookie instead of localStorage
  const cookies = document.cookie.split(";")
  return cookies.some((cookie) =>
    cookie.trim().startsWith(`${ACCESS_TOKEN_COOKIE_NAME}=`),
  )
}

const getDefaultRouteForRole = (user: AuthUser | null): string => {
  if (!user) return "/dashboard"

  const role = String(
    user.role ?? (user.is_superuser ? "admin" : ""),
  ).toLowerCase()
  const department = String(
    user.department ?? user.staff_type ?? user.staff_role ?? "",
  ).toLowerCase()

  switch (role) {
    case "admin":
      return "/dashboard"
    case "taxi_staff":
      return "/calendar/taxi"
    case "dock_staff":
      return "/calendar/kayak"
    case "kitchen_staff":
      return "/calendar/kitchen"
    case "manager":
      return "/dashboard"
    case "booking_coordinator":
    case "coordinator":
      return "/bookings"
    case "staff":
      if (department === "kitchen") return "/calendar/kitchen"
      if (department === "kayak") return "/calendar/kayak"
      return "/calendar/taxi"
    default:
      return "/dashboard"
  }
}

type UseAuthResult = {
  loginMutation: UseMutationResult<unknown, Error, AccessToken, unknown>
  logout: () => void
  user: AuthUser | null
}

const useAuth = (): UseAuthResult => {
  const queryClient = useQueryClient()
  const { showErrorToast } = useCustomToast()
  const { setUser, setLoading, clearAuth, user: storedUser } = useAuthStore()

  const { data: user, error } = useQuery<UserPublic | null, Error>({
    queryKey: ["currentUser"],
    queryFn: AuthenticationService.getCurrentUserInfo,
    retry: false,
  })

  useEffect(() => {
    if (user) {
      console.log("[DEBUG] useQuery fetched user:", user)
      setUser(user)
    }
  }, [user, setUser])

  useEffect(() => {
    if (error) {
      console.log("[DEBUG] useQuery error:", error)
      // Only clear auth if it's a real authentication error, not a transient error
      if (error.message?.includes("401") || error.message?.includes("403")) {
        clearAuth()
      }
    }
  }, [error, clearAuth])

  // Login function - tokens are now set as HTTP-only cookies by the backend
  const login = async (data: AccessToken) => {
    setLoading(true)
    console.log("[DEBUG] Calling AuthenticationService.login...")
    // Use AuthenticationService.login which sets HTTP-only cookies
    const response = await AuthenticationService.login({
      formData: data,
    })
    console.log("[DEBUG] Login successful, cookies should be set:", response)
    setLoading(false)
    return response
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async () => {
      console.log("[DEBUG] Login mutation onSuccess called")
      // Force refetch the current user
      await queryClient.invalidateQueries({ queryKey: ["currentUser"] })

      // Wait a bit for the useQuery to refetch
      await new Promise((resolve) => setTimeout(resolve, 100))

      const currentUser = queryClient.getQueryData<AuthUser>(["currentUser"])
      console.log("[DEBUG] Current user from cache:", currentUser)

      if (currentUser) {
        setUser(currentUser)
        const route = getDefaultRouteForRole(currentUser)
        console.log("[DEBUG] Navigating to:", route)
        window.location.href = route
      } else {
        // Fallback: get user directly
        try {
          const userData = await AuthenticationService.getCurrentUserInfo()
          const authUser = userData as AuthUser
          setUser(authUser)
          const route = getDefaultRouteForRole(authUser)
          console.log("[DEBUG] Direct fetch user, navigating to:", route)
          window.location.href = route
        } catch (_error) {
          console.log("[DEBUG] Failed to fetch user after login")
          showErrorToast("Login succeeded but failed to load user profile.")
          window.location.href = "/dashboard"
        }
      }
    },
    onError: (error: Error) => {
      console.log("[DEBUG] Login mutation error:", error)
      showErrorToast(error.message || "Login failed")
    },
    onSettled: () => {
      console.log("[DEBUG] Login mutation settled")
      setLoading(false)
    },
  })

  const logout = async () => {
    try {
      await AuthenticationService.logout()
    } catch (_error) {
      // Continue with logout even if the API call fails
    }
    clearAuth()
    queryClient.clear()
    window.location.href = "/login"
  }

  const resolvedUser: AuthUser | null = storedUser ?? user ?? null

  return {
    loginMutation,
    logout,
    user: resolvedUser,
  }
}

export { getDefaultRouteForRole, isLoggedIn }
export default useAuth
